import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faArrowsRotate,
    faCircleCheck,
    faCircleMinus,
    faCircleXmark,
    faCopy,
    faPlugCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';
import {useSnackbar} from 'notistack';

import {useApplicationContext} from 'ApplicationContext';
import Title from 'Components/Title';
import {
    O365PermissionsDto,
    O365SettingsDto,
    O365TestResultDto,
    UpdateO365SettingsRequest,
} from 'apiClient/Api';

/**
 * Vorgabewerte für ein leeres Formular. Bewusst harmlos: abgeschaltet, nichts eingetragen.
 */
const emptyForm: UpdateO365SettingsRequest = {
    enabled: false,
    tenantId: '',
    clientId: '',
    clientSecret: null,
    timeoutSeconds: 30,
};

const toForm = (dto: O365SettingsDto): UpdateO365SettingsRequest => ({
    enabled: dto.enabled ?? false,
    tenantId: dto.tenantId ?? '',
    clientId: dto.clientId ?? '',
    // null heißt "gespeicherten Schlüssel unverändert lassen" — nie den alten Wert vortäuschen.
    clientSecret: null,
    timeoutSeconds: dto.timeoutSeconds ?? 30,
});

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Überschrift eines Einrichtungsschritts: Nummer im Kreis plus Titel. */
const StepTitle: React.FC<{nr: number; title: string; optional?: boolean}> = ({nr, title, optional}) => (
    <Stack direction="row" spacing={1.5} sx={{alignItems: 'center'}}>
        <Box sx={{
            width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0,
        }}>
            {nr}
        </Box>
        <Typography variant="subtitle1" sx={{fontWeight: 600}}>{title}</Typography>
        {optional && <Chip size="small" variant="outlined" label="empfohlen, aber optional"/>}
    </Stack>
);

/** Code-/Befehlsblock mit Kopierknopf — für die PowerShell-Befehle der Zugriffsrichtlinie. */
const CodeBlock: React.FC<{code: string}> = ({code}) => {
    const {enqueueSnackbar} = useSnackbar();

    const copy = (): void => {
        navigator.clipboard.writeText(code)
            .then(() => enqueueSnackbar('Kopiert.', {variant: 'success'}))
            .catch(() => enqueueSnackbar('Kopieren fehlgeschlagen — bitte von Hand markieren.', {variant: 'error'}));
    };

    return (
        <Box sx={{position: 'relative', bgcolor: 'action.hover', borderRadius: 1, p: 1.5, pr: 6}}>
            <Tooltip title="In die Zwischenablage kopieren">
                <IconButton size="small" onClick={copy} sx={{position: 'absolute', top: 6, right: 6}}>
                    <FontAwesomeIcon icon={faCopy}/>
                </IconButton>
            </Tooltip>
            <Typography component="pre" sx={{
                m: 0, fontFamily: 'monospace', fontSize: '0.8rem',
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>
                {code}
            </Typography>
        </Box>
    );
};

/**
 * Zeigt, welche Graph-Berechtigungen wirklich erteilt sind — und welche Funktion daran hängt.
 *
 * Die Auskunft kommt aus dem Zugriffstoken, das Microsoft ausstellt: dort stehen die erteilten
 * Anwendungsberechtigungen. Das ist der einzige Weg, der den Stand **nach** der
 * Administratorzustimmung zeigt. Im Portal sieht eine hinzugefügte, aber nicht zugestimmte
 * Berechtigung fast genauso aus wie eine fertige — hier taucht sie schlicht nicht auf.
 */
const PermissionPanel: React.FC<{
    permissions: O365PermissionsDto | null;
    loading: boolean;
    complete: boolean;
    onReload: () => void;
}> = ({permissions, loading, complete, onReload}) => {
    // Alles, was erteilt ist, aber vom Support-System nicht benutzt wird. Der App-Registrierung
    // mehr zu geben als sie braucht, ist kein Fehler des Systems — sichtbar sein sollte es trotzdem.
    const unused = useMemo(() => {
        const known = new Set((permissions?.capabilities ?? []).map((c) => (c.name ?? '').toLowerCase()));
        return (permissions?.granted ?? []).filter((g) => !known.has(g.toLowerCase()));
    }, [permissions]);

    if (!complete) {
        return (
            <Typography variant="body2" color="text.secondary">
                Sobald Verzeichnis-ID, Anwendungs-ID und Clientschlüssel gespeichert sind, steht hier, welche
                Berechtigungen tatsächlich erteilt sind.
            </Typography>
        );
    }

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', mb: 0.5}}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>Tatsächlich erteilt</Typography>
                <Button size="small" variant="text" onClick={onReload} disabled={loading}
                        startIcon={<FontAwesomeIcon icon={faArrowsRotate}/>}>
                    {loading ? 'Frage ab …' : 'Neu abfragen'}
                </Button>
            </Stack>

            {loading && !permissions && <LinearProgress/>}

            {permissions && !permissions.available && (
                <Alert severity="warning">{permissions.message}</Alert>
            )}

            {permissions?.available && (
                <>
                    {(permissions.granted ?? []).length === 0 && (
                        <Alert severity="error" sx={{mb: 1}}>{permissions.message}</Alert>
                    )}

                    <List dense sx={{py: 0}}>
                        {(permissions.capabilities ?? []).map((capability) => (
                            <ListItem key={capability.name} sx={{py: 0.25, alignItems: 'flex-start'}}>
                                <ListItemIcon sx={{
                                    minWidth: 28,
                                    mt: 0.5,
                                    color: capability.granted
                                        ? 'success.main'
                                        : capability.required ? 'error.main' : 'text.disabled',
                                }}>
                                    <FontAwesomeIcon icon={
                                        capability.granted
                                            ? faCircleCheck
                                            : capability.required ? faCircleXmark : faCircleMinus
                                    }/>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Stack direction="row" spacing={1}
                                               sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5}}>
                                            <Box component="code">{capability.name}</Box>
                                            {!capability.granted && capability.required && (
                                                <Chip size="small" color="error" label="fehlt — Pflicht"/>
                                            )}
                                            {!capability.granted && !capability.required && (
                                                <Chip size="small" variant="outlined" label="nicht erteilt"/>
                                            )}
                                        </Stack>
                                    }
                                    secondary={capability.purpose}/>
                            </ListItem>
                        ))}
                    </List>

                    {unused.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                            Zusätzlich erteilt, vom Support-System aber nicht benutzt: {unused.join(', ')}.
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
};

/**
 * Einrichtung der Microsoft-365-Anbindung: Outlook-Kalender der Mitarbeiter über Microsoft Graph.
 *
 * Der Weg ist bewusst **zentral über den Admin** (Application Permissions statt pro-Kopf-OAuth):
 * einmal eine App im Entra-Portal registrieren, `Calendars.Read` erteilen — danach muss kein
 * Mitarbeiter irgendetwas verbinden. Die Person ergibt sich aus dem AD-Konto
 * (`UserPrincipalName`), das per Azure-AD-Connect mit Microsoft 365 synchron ist.
 *
 * Vorerst nur lesend. Alle Werte greifen ohne Server-Neustart: sie liegen in `ServerSettings`
 * und überlagern die Startwerte aus `appsettings.json` — wie bei allen Anbindungen ist das der
 * einzige Weg, der in Produktion funktioniert.
 */
export const O365Settings: React.FC = () => {
    const {api, apiError, setTitle} = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [settings, setSettings] = useState<O365SettingsDto | null>(null);
    const [form, setForm] = useState<UpdateO365SettingsRequest>(emptyForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testUpn, setTestUpn] = useState('');
    const [testResult, setTestResult] = useState<O365TestResultDto | null>(null);
    const [permissions, setPermissions] = useState<O365PermissionsDto | null>(null);
    const [permissionsLoading, setPermissionsLoading] = useState(false);

    useEffect(() => setTitle('Microsoft 365'), [setTitle]);

    const reload = useCallback(() => {
        setLoading(true);
        api.o365
            .o365GetSettings()
            .then((r) => {
                setSettings(r.data);
                setForm(toForm(r.data));
            })
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, apiError]);

    useEffect(() => reload(), [reload]);

    /**
     * Fragt bei Microsoft nach, welche Berechtigungen tatsächlich erteilt sind.
     *
     * Antwortet auch im Fehlerfall mit HTTP 200 — die Begründung steht in der Antwort und gehört
     * in die Anzeige, nicht in eine Fehlermeldung am Bildschirmrand.
     */
    const loadPermissions = useCallback(() => {
        setPermissionsLoading(true);
        api.o365
            .o365Permissions()
            .then((r) => setPermissions(r.data))
            .catch(apiError)
            .finally(() => setPermissionsLoading(false));
    }, [api, apiError]);

    // Erst fragen, wenn die Zugangsdaten vollständig sind: vorher gäbe es nur die Meldung, dass
    // Pflichtangaben fehlen — die steht schon oben.
    useEffect(() => {
        if (settings?.isComplete) loadPermissions();
    }, [settings?.isComplete, loadPermissions]);

    const patch = (changes: Partial<UpdateO365SettingsRequest>) =>
        setForm((prev) => ({...prev, ...changes}));

    const hasSecret = settings?.clientSecretConfigured ?? false;

    /** Klientseitige Vorprüfung — dieselben Grenzen, die der Server durchsetzt. */
    const problems = useMemo(() => {
        const out: string[] = [];
        const tenant = (form.tenantId ?? '').trim();
        const client = (form.clientId ?? '').trim();

        if (client && !GUID_PATTERN.test(client)) {
            out.push('Die Anwendungs-ID (Client) muss eine GUID sein — sie steht in der Übersicht der App-Registrierung.');
        }
        if (tenant && !GUID_PATTERN.test(tenant) && !tenant.includes('.')) {
            out.push('Die Verzeichnis-ID (Mandant) muss eine GUID oder eine Domain wie firma.onmicrosoft.com sein.');
        }
        if ((form.timeoutSeconds ?? 0) < 1 || (form.timeoutSeconds ?? 0) > 120) {
            out.push('Das Zeitlimit muss zwischen 1 und 120 Sekunden liegen.');
        }
        if (form.enabled) {
            if (!tenant || !client) {
                out.push('Ohne Verzeichnis-ID und Anwendungs-ID lässt sich die Anbindung nicht einschalten.');
            }
            const secretMissing = form.clientSecret === null ? !hasSecret : form.clientSecret === '';
            if (secretMissing) {
                out.push('Ohne geheimen Clientschlüssel lässt sich die Anbindung nicht einschalten.');
            }
        }
        return out;
    }, [form, hasSecret]);

    const save = async (): Promise<void> => {
        setSaving(true);
        try {
            const response = await api.o365.o365SaveSettings(form);
            setSettings(response.data);
            setForm(toForm(response.data));
            enqueueSnackbar('Gespeichert — greift ohne Neustart.', {variant: 'success'});
        } catch (e: any) {
            // Der Server antwortet bei Validierungsfehlern mit Klartext.
            const detail = e?.response?.status === 400 ? e?.response?.data : null;
            if (typeof detail === 'string' && detail.length > 0) {
                enqueueSnackbar(detail, {variant: 'error'});
            } else {
                apiError(e);
            }
        } finally {
            setSaving(false);
        }
    };

    /**
     * Der Test läuft immer gegen den **gespeicherten** Stand — ungespeicherte Feldänderungen
     * sieht er nicht. Deshalb warnt der Knopf, solange das Formular vom Server abweicht.
     */
    const dirty = useMemo(() => {
        if (!settings) return false;
        const saved = toForm(settings);
        return form.enabled !== saved.enabled
            || (form.tenantId ?? '') !== (saved.tenantId ?? '')
            || (form.clientId ?? '') !== (saved.clientId ?? '')
            || form.clientSecret !== null
            || form.timeoutSeconds !== saved.timeoutSeconds;
    }, [form, settings]);

    const runTest = async (): Promise<void> => {
        setTesting(true);
        setTestResult(null);
        try {
            const response = await api.o365.o365TestConnection({
                userPrincipalName: testUpn.trim() || null,
            });
            setTestResult(response.data);
        } catch (e) {
            apiError(e);
        } finally {
            setTesting(false);
        }
    };

    const active = (settings?.enabled ?? false) && (settings?.isComplete ?? false);

    const presenceGranted = useMemo(
        () => (permissions?.granted ?? []).some((g) => g.toLowerCase() === 'presence.read.all'),
        [permissions]);

    const accessPolicySnippet = useMemo(() => {
        const clientId = (form.clientId ?? '').trim() || '<Anwendungs-ID (Client)>';
        return [
            'Connect-ExchangeOnline',
            '',
            '# Mail-aktivierte Sicherheitsgruppe mit allen Mitarbeitern, deren Kalender gelesen werden darf.',
            '# Eine bestehende Gruppe geht auch — sie muss nur mail-aktiviert sein.',
            'New-DistributionGroup -Name "SupportSystem-Kalender" -Type Security',
            'Add-DistributionGroupMember -Identity "SupportSystem-Kalender" -Member max.mustermann@reichert-gmbh.com',
            '',
            '# Ab jetzt kommt die App nur noch an Postfächer dieser Gruppe heran:',
            `New-ApplicationAccessPolicy -AppId ${clientId} \\`,
            '    -PolicyScopeGroupId SupportSystem-Kalender@reichert-gmbh.com \\',
            '    -AccessRight RestrictAccess \\',
            '    -Description "Support-System liest nur die Kalender der Support-Mitarbeiter"',
            '',
            '# Prüfen (Granted = Zugriff, Denied = blockiert):',
            `Test-ApplicationAccessPolicy -Identity max.mustermann@reichert-gmbh.com -AppId ${clientId}`,
        ].join('\n');
    }, [form.clientId]);

    const stepIcon = (success?: boolean | null) =>
        success === true ? faCircleCheck : success === false ? faCircleXmark : faCircleMinus;

    return (
        <Grid size={12}>
            <Title>Microsoft 365 (Outlook-Kalender)</Title>
            {loading && <LinearProgress sx={{mb: 2}}/>}

            <Grid container spacing={2}>
                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                            <FontAwesomeIcon icon={active ? faCircleCheck : faCircleXmark}/>
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                {active ? 'Anbindung aktiv' : 'Anbindung inaktiv'}
                            </Typography>
                            {settings?.enabled && !settings?.isComplete && (
                                <Chip size="small" color="warning" label="unvollständig konfiguriert"/>
                            )}
                            <Chip size="small" variant="outlined" label="nur lesend (Calendars.Read)"/>
                            {presenceGranted && (
                                <Chip size="small" color="success" variant="outlined" label="Teams-Status aktiv"/>
                            )}
                            {settings?.tenantId && (
                                <Chip size="small" variant="outlined" label={`Mandant ${settings.tenantId}`}/>
                            )}
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
                            Liest die Outlook-Kalender der Mitarbeiter über Microsoft Graph, damit sie neben dem
                            Labelwin-Kalender angezeigt werden können — Teams-Besprechungen liegen im selben
                            Kalender und kommen automatisch mit. Die Einrichtung passiert einmal hier;{' '}
                            <strong>kein Mitarbeiter muss etwas verbinden oder freigeben</strong>. Wessen Kalender
                            gezeigt wird, bestimmt das AD-Konto der angemeldeten Person (Benutzerprinzipalname) —
                            lokale Konten ohne AD-Anmeldung haben keinen Outlook-Kalender.
                        </Typography>

                        {settings?.clientSecretUnreadable && (
                            <Alert severity="warning" sx={{mt: 2}}>
                                Es liegt ein gespeicherter Clientschlüssel vor, der sich nicht entschlüsseln lässt —
                                typisch nach neu erzeugten DataProtection-Keys. Bitte neu eintragen (Schritt 3).
                            </Alert>
                        )}
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={1.5}>
                            <StepTitle nr={1} title="App im Entra-Portal registrieren"/>
                            <Typography variant="body2">
                                Anmelden als Administrator auf{' '}
                                <a href="https://entra.microsoft.com" target="_blank" rel="noreferrer">
                                    entra.microsoft.com
                                </a>{' '}
                                → <strong>Identität → Anwendungen → App-Registrierungen</strong> →{' '}
                                <strong>Neue Registrierung</strong>.
                            </Typography>
                            <List dense sx={{py: 0}}>
                                <ListItem sx={{py: 0.25}}>
                                    <ListItemText
                                        primary="Name: z. B. „Reichert Support-System — Kalender“"
                                        secondary="Nur Anzeigename, frei wählbar."/>
                                </ListItem>
                                <ListItem sx={{py: 0.25}}>
                                    <ListItemText
                                        primary="Kontotypen: „Nur Konten in diesem Organisationsverzeichnis“ (Standard)"/>
                                </ListItem>
                                <ListItem sx={{py: 0.25}}>
                                    <ListItemText
                                        primary="Umleitungs-URI: leer lassen"
                                        secondary="Der Server meldet sich direkt mit dem Clientschlüssel an (Client-Credentials-Flow) — es gibt keinen Browser-Login und darum keine Umleitung."/>
                                </ListItem>
                            </List>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={1.5}>
                            <StepTitle nr={2} title="Verzeichnis-ID und Anwendungs-ID kopieren"/>
                            <Typography variant="body2">
                                Nach der Registrierung öffnet sich die <strong>Übersicht</strong> der App. Dort stehen
                                beide IDs — hierher kopieren:
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{xs: 12, md: 6}}>
                                    <TextField fullWidth size="small" label="Verzeichnis-ID (Mandant)"
                                               value={form.tenantId ?? ''}
                                               onChange={(e) => patch({tenantId: e.target.value})}
                                               placeholder="z. B. 5c8e2f10-…"
                                               helperText="Im Portal: „Verzeichnis-ID (Mandant)“ — identisch für alle Apps des Unternehmens."/>
                                </Grid>
                                <Grid size={{xs: 12, md: 6}}>
                                    <TextField fullWidth size="small" label="Anwendungs-ID (Client)"
                                               value={form.clientId ?? ''}
                                               onChange={(e) => patch({clientId: e.target.value})}
                                               placeholder="z. B. 8f31ab42-…"
                                               helperText="Im Portal: „Anwendungs-ID (Client)“ — gehört zu genau dieser App."/>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={1.5}>
                            <StepTitle nr={3} title="Geheimen Clientschlüssel erzeugen"/>
                            <Typography variant="body2">
                                In der App-Registrierung: <strong>Zertifikate &amp; Geheimnisse</strong> →{' '}
                                <strong>Neuer geheimer Clientschlüssel</strong> → Beschreibung eintragen, Laufzeit
                                wählen (höchstens 24 Monate).
                            </Typography>
                            <Alert severity="warning">
                                Nach dem Erzeugen den <strong>Wert</strong> kopieren, nicht die „Geheimnis-ID" —
                                der Wert ist <strong>nur in diesem Moment</strong> sichtbar. Und: der Schlüssel
                                läuft ab; am besten gleich eine Erinnerung vor dem Ablaufdatum eintragen, sonst
                                bleibt der Kalender an dem Tag kommentarlos leer.
                            </Alert>
                            <Stack direction="row" spacing={1} sx={{alignItems: 'flex-start'}}>
                                <TextField
                                    fullWidth size="small" type="password" label="Geheimer Clientschlüssel (Wert)"
                                    value={form.clientSecret ?? ''}
                                    disabled={form.clientSecret === null && hasSecret}
                                    onChange={(e) => patch({clientSecret: e.target.value})}
                                    placeholder={hasSecret ? '•••••••• (gespeichert)' : 'Wert aus „Zertifikate & Geheimnisse“'}
                                    helperText={form.clientSecret === null && hasSecret
                                        ? 'Hinterlegt. Zum Ersetzen auf „Ändern“ klicken — der gespeicherte Wert wird nie ausgeliefert.'
                                        : 'Wird verschlüsselt gespeichert (DataProtection) und nie wieder angezeigt.'}/>
                                {hasSecret && (
                                    <Button size="small" sx={{mt: 0.5}}
                                            onClick={() => patch({clientSecret: form.clientSecret === null ? '' : null})}>
                                        {form.clientSecret === null ? 'Ändern' : 'Abbrechen'}
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={1.5}>
                            <StepTitle nr={4} title="Berechtigungen erteilen"/>
                            <Typography variant="body2">
                                In der App-Registrierung: <strong>API-Berechtigungen</strong> →{' '}
                                <strong>Berechtigung hinzufügen</strong> → <strong>Microsoft Graph</strong> →{' '}
                                <strong>Anwendungsberechtigungen</strong> (nicht „delegiert"!) → nach{' '}
                                <code>Calendars.Read</code> suchen → ankreuzen → <strong>Berechtigungen
                                hinzufügen</strong>.
                            </Typography>
                            <Typography variant="body2">
                                Danach in derselben Ansicht auf{' '}
                                <strong>„Administratorzustimmung für &lt;Unternehmen&gt; erteilen"</strong> klicken,
                                bis in der Statusspalte ein grüner Haken steht. Ohne diesen Klick schlägt der
                                Kalenderzugriff mit HTTP 403 fehl, obwohl die Berechtigung eingetragen aussieht —
                                das ist der häufigste Stolperstein.
                            </Typography>
                            <Alert severity="info">
                                <AlertTitle>Später schreiben? Gleiche Stelle, eine Berechtigung mehr.</AlertTitle>
                                <code>Calendars.Read</code> ist reiner Lesezugriff. Sollen später Termine aus dem
                                Support-System in die Kalender der Mitarbeiter geschrieben werden (z. B. ein
                                Einsatz in den Kalender eines Technikers), wird hier{' '}
                                <code>Calendars.ReadWrite</code> ergänzt (schließt Lesen ein,{' '}
                                <code>Calendars.Read</code> kann dann entfallen) und die Administratorzustimmung
                                einmal neu erteilt. Mehr ändert sich nicht: keine neue App, kein neuer Schlüssel,
                                nichts an den Einstellungen hier. Die App schreibt dann als sie selbst in die
                                Postfächer der freigegebenen Gruppe — der Mitarbeiter muss nichts bestätigen, der
                                Termin erscheint in seinem Kalender wie ein selbst eingetragener. Im Support-System
                                bekommt der Schreibpfad einen eigenen Schalter (ab Werk aus) samt Audit-Log — die
                                Berechtigung allein schaltet nichts scharf.
                            </Alert>

                            <Alert severity="info">
                                <AlertTitle>Teams-Status in der Planungstafel</AlertTitle>
                                Mit der zusätzlichen Anwendungsberechtigung <code>Presence.Read.All</code> zeigt
                                die Planungstafel neben jedem Namen, ob die Person <strong>gerade</strong>{' '}
                                verfügbar, beschäftigt oder im Gespräch ist — der Kalender sagt nur, was{' '}
                                <em>geplant</em> war. Sie ist optional: fehlt sie, fällt die Anzeige stillschweigend
                                auf den aus dem Kalender gerechneten Zustand zurück, und es wird gar nicht erst bei
                                Microsoft angefragt. Erteilt wird sie genau wie oben — hinzufügen{' '}
                                <strong>und</strong> Administratorzustimmung erteilen.
                            </Alert>

                            <PermissionPanel
                                permissions={permissions}
                                loading={permissionsLoading}
                                complete={settings?.isComplete ?? false}
                                onReload={loadPermissions}/>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={1.5}>
                            <StepTitle nr={5} title="Zugriff auf die Mitarbeiter-Postfächer begrenzen" optional/>
                            <Typography variant="body2">
                                <code>Calendars.Read</code> als Anwendungsberechtigung gilt erst einmal für{' '}
                                <strong>alle Postfächer des Unternehmens</strong> — auch Geschäftsführung und
                                Personal. Eine Zugriffsrichtlinie in Exchange Online begrenzt die App auf eine
                                Sicherheitsgruppe; technisch kommt sie dann an kein anderes Postfach mehr heran.
                                Dazu einmalig in der Exchange Online PowerShell:
                            </Typography>
                            <CodeBlock code={accessPolicySnippet}/>
                            <Typography variant="body2" color="text.secondary">
                                Die Richtlinie greift nach wenigen Minuten. Neue Mitarbeiter bekommen den Kalender,
                                indem man sie in die Gruppe aufnimmt — hier muss dann nichts geändert werden. Und:
                                die Richtlinie unterscheidet nicht zwischen Lesen und Schreiben, sie begrenzt, an
                                welche Postfächer die App überhaupt herankommt — sie gilt also unverändert auch für
                                den späteren Schreibzugriff mit <code>Calendars.ReadWrite</code>. Auch deshalb
                                lohnt es sich, sie jetzt schon einzurichten.
                            </Typography>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{p: 2}}>
                        <Stack spacing={1.5}>
                            <StepTitle nr={6} title="Speichern, testen, einschalten"/>
                            <Typography variant="body2">
                                Erst <strong>speichern</strong> (der Test prüft den gespeicherten Stand), dann{' '}
                                <strong>testen</strong>. Der Test läuft in zwei Stufen: Anmeldung bei Microsoft
                                (prüft Mandant, Client-ID und Schlüssel — Schritte 1–3), dann Kalenderzugriff auf
                                ein Postfach (prüft Berechtigung, Zustimmung und Zugriffsrichtlinie — Schritte
                                4–5). Wenn beides grün ist, die Anbindung einschalten und erneut speichern.
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={{xs: 12, md: 4}}>
                                    <TextField fullWidth size="small" type="number" label="Zeitlimit (s)"
                                               value={form.timeoutSeconds ?? 30}
                                               onChange={(e) => patch({timeoutSeconds: Number(e.target.value)})}
                                               helperText="1–120, je Graph-Anfrage"/>
                                </Grid>
                                <Grid size={{xs: 12, md: 8}}>
                                    <TextField fullWidth size="small" label="Postfach für den Test (optional)"
                                               value={testUpn}
                                               onChange={(e) => setTestUpn(e.target.value)}
                                               placeholder="max.mustermann@reichert-gmbh.com"
                                               helperText="Leer = das eigene AD-Konto. Angeben, wenn Ihr Admin-Konto kein AD-Konto ist oder Sie ein bestimmtes Postfach prüfen wollen."/>
                                </Grid>
                            </Grid>

                            <FormControlLabel
                                control={<Checkbox checked={form.enabled ?? false}
                                                   onChange={(e) => patch({enabled: e.target.checked})}/>}
                                label="Anbindung aktiv"/>

                            {problems.length > 0 && (
                                <Alert severity="warning">
                                    <AlertTitle>Bitte prüfen</AlertTitle>
                                    {problems.map((problem) => <div key={problem}>{problem}</div>)}
                                </Alert>
                            )}

                            <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 1}}>
                                <Button variant="contained" onClick={save}
                                        disabled={saving || loading || problems.length > 0}>
                                    {saving ? 'Speichere …' : 'Speichern'}
                                </Button>
                                <Tooltip title={dirty ? 'Das Formular hat ungespeicherte Änderungen — der Test prüft den gespeicherten Stand.' : ''}>
                                    <span>
                                        <Button variant="outlined" onClick={runTest}
                                                disabled={testing || loading}
                                                color={dirty ? 'warning' : 'primary'}
                                                startIcon={<FontAwesomeIcon icon={faPlugCircleCheck}/>}>
                                            {testing ? 'Teste …' : 'Verbindung testen'}
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button variant="text" onClick={reload} disabled={saving || loading}>
                                    Verwerfen
                                </Button>
                            </Stack>

                            {testResult && (
                                <Alert severity={testResult.success ? 'success' : 'error'}>
                                    <AlertTitle>
                                        {testResult.success ? 'Test erfolgreich' : 'Test fehlgeschlagen'}
                                    </AlertTitle>
                                    {testResult.message}
                                    <List dense sx={{py: 0, mt: 1}}>
                                        {(testResult.steps ?? []).map((step) => (
                                            <ListItem key={step.name} sx={{py: 0.25, alignItems: 'flex-start'}}>
                                                <ListItemIcon sx={{minWidth: 28, mt: 0.5}}>
                                                    <FontAwesomeIcon icon={stepIcon(step.success)}/>
                                                </ListItemIcon>
                                                <ListItemText primary={step.name} secondary={step.detail}/>
                                            </ListItem>
                                        ))}
                                    </List>
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                                        Dauer: {testResult.elapsedMilliseconds} ms
                                        {testResult.userPrincipalName && ` · Postfach: ${testResult.userPrincipalName}`}
                                    </Typography>
                                </Alert>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default O365Settings;
